import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MENU } from './menu';
import { MenuItem } from './menu.model';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { getWorkplaceUserCompanies } from 'src/app/core/utils/account-organization.util';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})

export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  isGlobalAdmin: boolean;
  currentUserName = '';
  currentUserEmail = '';
  currentUserInitials = '';
  currentCompanyName = '';
  navOpen = false;
  showUserPopup = false;
  showCompanySwitch = false;
  loggingOut = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _appContextService: ApplicationContextService,
    private readonly _router: Router,
    private readonly _authService: AuthenticationService,
    private readonly _toastService: ToastrService
  ) {
    this.menuItems = MENU;
  }

  ngOnInit(): void {
    this._appContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        this.isGlobalAdmin = context.currentlyLoggedInUser?.globalAdmin;
        const user = context.currentlyLoggedInUser;
        if (!user) {
          return;
        }
        this.currentUserName = `${user.firstName} ${user.lastName}`.trim();
        this.currentUserEmail = user.email || '';
        const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || '';
        const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || '';
        this.currentUserInitials = `${firstInitial}${lastInitial}`;
        this.currentCompanyName = user.companyName || '';
        this.showCompanySwitch =
          getWorkplaceUserCompanies(context.userCompanies).length > 1;
      });

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.closeNav());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  menuItemClicked(menuItem: MenuItem) {
    if (!menuItem.hasExternalLink) {
      return;
    }
    const linkWithToken = this._authService.appendEncryptedAuthInfo(menuItem.link);
    window.open(linkWithToken, '_blank');
  }

  openSettingsApp() {
    const linkWithToken = this._authService.appendEncryptedAuthInfo(environment.adminAppUrl);
    window.open(linkWithToken, '_blank');
  }

  isCurrentRoute(route: string): boolean {
    return this._router.url === route;
  }

  toggleNav(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.navOpen = !this.navOpen;
    if (this.navOpen) {
      this.closeUserPopup();
    }
  }

  closeNav() {
    this.navOpen = false;
  }

  toggleUserPopup(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showUserPopup = !this.showUserPopup;
  }

  closeUserPopup() {
    this.showUserPopup = false;
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.showUserPopup) {
      this.closeUserPopup();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.navOpen) {
      this.closeNav();
    }
    if (this.showUserPopup) {
      this.closeUserPopup();
    }
  }

  gotoSelectCompanyPage() {
    this.closeUserPopup();
    void this._router.navigateByUrl('/select-company?reason=switch');
  }

  logout() {
    if (this.loggingOut) {
      return;
    }
    this.closeUserPopup();
    this.loggingOut = true;
    this._authService.logoutAllApps().subscribe({
      next: () => {
        this._authService.logout(true);
      },
      error: (err) => {
        this._toastService.error(err || 'unable to log user out.', 'Error!');
      }
    }).add(() => this.loggingOut = false);
  }
}
